import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography, Modal, Button, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useSidebarContext } from '../../../../../contexts/Old_SidebarContext'
import { Link } from 'react-router-dom'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

interface Props {
  message: any
  currentSource: {
    content: string[]
    title: string
    docLink: string
  }
}

const MarkdownRenderer = (props: any) => {
  /* eslint-disable react/prop-types */
  const { textToDisplay, currentSource, setContent, handleOpen } = props
  const {
    setSendIsLoading,
    setLastAskedQuestion,
    setAreFilesSelected,
    setUpdateQuestion,
    setIsLoading,
  } = useSidebarContext()

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)

  // console.log('355 textToDisplay', textToDisplay)
  // console.log('message', message)

  useEffect(() => {
    textToDisplay?.length > 0 && setSendIsLoading(false)
    textToDisplay?.length > 0 && setLastAskedQuestion('')
    textToDisplay?.length > 0 && setAreFilesSelected(false)
    textToDisplay?.length > 0 && setUpdateQuestion(false)
    textToDisplay?.length > 0 && setIsLoading(false)
  }, [textToDisplay])

  // Reset slide when currentSource changes
  useEffect(() => {
    setCurrentSlide(0)
  }, [currentSource])

  // Keyboard navigation for carousel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!carouselRef.current) return

      const sourcesArray = Array.isArray(currentSource) ? currentSource : []
      const totalSlides = sourcesArray.length

      if (totalSlides <= 1) return

      if (event.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev > 0 ? prev - 1 : totalSlides - 1))
      } else if (event.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev < totalSlides - 1 ? prev + 1 : 0))
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentSource])

  // Carousel navigation handlers
  const handlePrevSlide = () => {
    const sourcesArray = Array.isArray(currentSource) ? currentSource : []
    const totalSlides = sourcesArray.length
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : totalSlides - 1))
  }

  const handleNextSlide = () => {
    const sourcesArray = Array.isArray(currentSource) ? currentSource : []
    const totalSlides = sourcesArray.length
    setCurrentSlide((prev) => (prev < totalSlides - 1 ? prev + 1 : 0))
  }

  // Custom resizable table component
  const ResizableTable = ({ children, ...tableProps }: any) => {
    const tableRef = useRef<HTMLTableElement>(null)

    useEffect(() => {
      const table = tableRef.current
      if (!table) return

      const headers = table.querySelectorAll('th')
      headers.forEach((th, index) => {
        // Skip adding resizer to the last column
        if (index === headers.length - 1) return

        const thElement = th as HTMLElement
        thElement.style.position = 'relative'

        // Create resize handle
        const resizer = document.createElement('div')
        resizer.style.position = 'absolute'
        resizer.style.top = '0'
        resizer.style.right = '0'
        resizer.style.width = '5px'
        resizer.style.height = '100%'
        resizer.style.cursor = 'col-resize'
        resizer.style.userSelect = 'none'
        resizer.style.backgroundColor = 'transparent'
        resizer.style.zIndex = '1'

        // Hover effect
        resizer.addEventListener('mouseenter', () => {
          resizer.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
        })
        resizer.addEventListener('mouseleave', () => {
          if (!resizer.classList.contains('resizing')) {
            resizer.style.backgroundColor = 'transparent'
          }
        })

        let startX: number, startWidth: number

        const onMouseDown = (e: MouseEvent) => {
          startX = e.clientX
          startWidth = thElement.offsetWidth
          resizer.classList.add('resizing')
          resizer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
          document.addEventListener('mousemove', onMouseMove)
          document.addEventListener('mouseup', onMouseUp)
          e.preventDefault()
        }

        const onMouseMove = (e: MouseEvent) => {
          const width = startWidth + (e.clientX - startX)
          if (width > 50) {
            // Minimum width
            thElement.style.width = `${width}px`
            thElement.style.minWidth = `${width}px`
          }
        }

        const onMouseUp = () => {
          resizer.classList.remove('resizing')
          resizer.style.backgroundColor = 'transparent'
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
        }

        resizer.addEventListener('mousedown', onMouseDown)
        thElement.appendChild(resizer)
      })
    }, [children])

    return (
      <table ref={tableRef} {...tableProps}>
        {children}
      </table>
    )
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100%',
          width: '100%',
          overflow: 'auto',
          overflowX: 'hidden',
          textAlign: 'justify',
          padding: {
            xs: '10px 0px',
            sm: '10px',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginTop: '1rem',
            marginBottom: '1rem',
            border: '1px solid #ddd',
            tableLayout: 'fixed', // Enable fixed layout for resizing
          },
          '& th': {
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            padding: '12px',
            textAlign: 'left',
            fontWeight: 'bold',
            position: 'relative',
          },
          '& td': {
            border: '1px solid #ddd',
            padding: '12px',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& tr:nth-of-type(even)': {
            backgroundColor: '#f9f9f9',
          },
          '& tr:hover': {
            backgroundColor: '#f0f0f0',
          },
        }}
      >
        {/* <Box sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}> */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ResizableTable,
          }}
        >
          {textToDisplay}
        </ReactMarkdown>
        {/* </Box> */}
        {currentSource?.title === 'web' && (
          <Box sx={{ border: '1px solid lightgrey', borderRadius: '4px', p: 1 }}>
            <h5 style={{ margin: 0, padding: 0 }}>Sources :</h5>
            {currentSource?.content?.map((item: string, index: number) => (
              <p
                key={index}
                style={{ cursor: 'pointer', fontSize: '14px', padding: 0, margin: 0 }}
                onClick={() => window.open(item, '_blank')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>
                    <OpenInNewIcon sx={{ fontSize: '18px' }} />
                  </span>
                  <span style={{ textDecoration: 'underline', marginTop: '-7px' }}>{item}</span>
                </Box>
              </p>
            ))}
          </Box>
        )}

        {currentSource?.title !== 'web' &&
          currentSource?.title !== 'Disclaimer' &&
          currentSource?.title !== 'Non-legal' && (
            <Box sx={{ border: '1px solid lightgrey', borderRadius: '4px', p: 1 }}>
              <h5 style={{ margin: 0, padding: 0 }}>Source :</h5>
              <span>
                {currentSource?.title}

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}
                >
                  {currentSource?.content.map((item: string, index: number) => (
                    <Tooltip
                      title={item.substring(0, 300)}
                      key={index}
                      placement='top'
                      onClick={() => {
                        // console.log('item', item)
                        setContent(item)
                        handleOpen()
                      }}
                    >
                      <Typography
                        // onMouseOver={() => {
                        //   setContent(item)
                        //   handleOpen()
                        // }}
                        // onMouseOut={() => {
                        //   handleClose()
                        // }}
                        key={index}
                        variant='body2'
                        color='initial'
                        sx={{
                          fontSize: '12px',
                          border: '0.5px solid grey',
                          borderRadius: '8px',
                          px: 1,
                          '&:hover': {
                            backgroundColor: '#587dbd',
                            color: '#FFF',
                          },
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Tooltip>
                  ))}
                </Box>
                {currentSource?.docLink && (
                  <Box sx={{ mt: 1 }}>
                    <Link
                      to={currentSource?.docLink}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ marginTop: '5px', textDecoration: 'none' }}
                    >
                      <Typography variant='subtitle1' color='initial'>
                        <span style={{ fontWeight: 'bold', color: '#777' }}>Link : </span>{' '}
                        {currentSource?.docLink}
                      </Typography>
                    </Link>
                  </Box>
                )}
              </span>
            </Box>
          )}

        {currentSource?.title === 'Disclaimer' && (
          <Box sx={{ border: '1px solid lightgrey', borderRadius: '4px', p: 1 }}>
            <h5 style={{ margin: 0, padding: 0 }}>Source :</h5>
            <span>
              {/* {currentSource?.title} */}
              {/* {currentSource?.docLink} */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                {currentSource?.content[0]}
              </Box>
            </span>
          </Box>
        )}

        {currentSource?.title === 'Non-legal' && (
          <Box sx={{ border: '1px solid lightgrey', borderRadius: '4px', p: 1 }}>
            {/* <h5 style={{ margin: 0, padding: 0 }}>Source :</h5> */}
            <span>
              {/* {currentSource?.title} */}
              {/* {currentSource?.docLink} */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}> */}
              {currentSource?.content[0]}
              {/* </Box> */}
            </span>
          </Box>
        )}
      </Box>
    </>
  )
}

const Message = ({ message, currentSource }: Props) => {
  // console.log('message :-', message)
  // console.log(' currentSource :-', currentSource)
  const { showFileUploadBox, openSpeechToText } = useSidebarContext()
  const { source } = useSidebarContext()

  const [open, setOpen] = React.useState(false)

  const [content, setContent] = useState('')

  const bottomRef = useRef<HTMLDivElement>(null)

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  // Custom resizable table component for modal
  const ModalResizableTable = ({ children, ...tableProps }: any) => {
    const tableRef = useRef<HTMLTableElement>(null)

    useEffect(() => {
      const table = tableRef.current
      if (!table) return

      const headers = table.querySelectorAll('th')
      headers.forEach((th, index) => {
        // Skip adding resizer to the last column
        if (index === headers.length - 1) return

        const thElement = th as HTMLElement
        thElement.style.position = 'relative'

        // Create resize handle
        const resizer = document.createElement('div')
        resizer.style.position = 'absolute'
        resizer.style.top = '0'
        resizer.style.right = '0'
        resizer.style.width = '5px'
        resizer.style.height = '100%'
        resizer.style.cursor = 'col-resize'
        resizer.style.userSelect = 'none'
        resizer.style.backgroundColor = 'transparent'
        resizer.style.zIndex = '1'

        // Hover effect
        resizer.addEventListener('mouseenter', () => {
          resizer.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
        })
        resizer.addEventListener('mouseleave', () => {
          if (!resizer.classList.contains('resizing')) {
            resizer.style.backgroundColor = 'transparent'
          }
        })

        let startX: number, startWidth: number

        const onMouseDown = (e: MouseEvent) => {
          startX = e.clientX
          startWidth = thElement.offsetWidth
          resizer.classList.add('resizing')
          resizer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
          document.addEventListener('mousemove', onMouseMove)
          document.addEventListener('mouseup', onMouseUp)
          e.preventDefault()
        }

        const onMouseMove = (e: MouseEvent) => {
          const width = startWidth + (e.clientX - startX)
          if (width > 50) {
            // Minimum width
            thElement.style.width = `${width}px`
            thElement.style.minWidth = `${width}px`
          }
        }

        const onMouseUp = () => {
          resizer.classList.remove('resizing')
          resizer.style.backgroundColor = 'transparent'
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
        }

        resizer.addEventListener('mousedown', onMouseDown)
        thElement.appendChild(resizer)
      })
    }, [children])

    return (
      <table ref={tableRef} {...tableProps}>
        {children}
      </table>
    )
  }

  useEffect(() => {
    // console.log('showFileUploadBox', showFileUploadBox)
    showFileUploadBox && bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showFileUploadBox])

  useEffect(() => {
    // console.log('showSpeechToTextDialogBox', showSpeechToTextDialogBox)
    openSpeechToText && bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [openSpeechToText])
  // currentSource && console.log('currentSource', currentSource)

  // return <div>{renderFormattedText(message)}</div>
  return (
    <>
      <MarkdownRenderer
        textToDisplay={message}
        currentSource={currentSource}
        setContent={setContent}
        handleOpen={handleOpen}
      />
      {showFileUploadBox && <div ref={bottomRef} />}

      <Modal
        // hideBackdrop={true}
        disableAutoFocus
        disableEnforceFocus={true}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 0, right: 0 }}>
            <CloseIcon sx={{ color: 'red', fonsize: '20px' }} />
          </IconButton>
          <Box
            sx={{
              wordWrap: 'break-word',
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                marginTop: '1rem',
                marginBottom: '1rem',
                border: '1px solid #ddd',
                tableLayout: 'fixed',
              },
              '& th': {
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                padding: '12px',
                textAlign: 'left',
                fontWeight: 'bold',
                position: 'relative',
              },
              '& td': {
                border: '1px solid #ddd',
                padding: '12px',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& tr:nth-of-type(even)': {
                backgroundColor: '#f9f9f9',
              },
              '& tr:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ModalResizableTable,
              }}
            >
              {content}
            </ReactMarkdown>
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default Message

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  maxHeight: '60vh',
  maxWidth: '70vw',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  borderRadius: '4px',
  p: 4,
  overflow: 'auto',
}
